export type Bilingual = {
  en: string
  fr: string
}

export const methodIntro = {
  eyebrow: { en: 'Our method', fr: 'Notre méthode' },
  headlineLead: { en: 'Step by step.', fr: 'Avancer par étapes.' },
  headlineAccent: { en: 'Real results.', fr: 'De vrais résultats.' },
  intro: {
    en: 'We analyze how work actually gets done — across systems, data and processes — then simplify, integrate and introduce AI step by step, with measurable gains at every stage.',
    fr: 'Nous analysons comment le travail se fait réellement — systèmes, données et processus — puis nous simplifions, intégrons et ajoutons l’IA, étape par étape, avec des gains concrets à chaque jalon.',
  },
  horizon: {
    en: 'Today, tangible gains. Tomorrow, even more possibilities.',
    fr: 'Aujourd’hui, des gains tangibles. Demain, encore plus de possibilités.',
  },
  /** Ordered from the base of the path to the summit. */
  milestones: [
    { en: 'A clear starting point', fr: 'Un point de départ clair' },
    { en: 'Less complexity', fr: 'Moins de complexité' },
    { en: 'More capacity', fr: 'Plus de capacité' },
    { en: 'More impact', fr: 'Plus d’impact' },
  ],
}

export type MethodStepIcon =
  | 'measure'
  | 'simplify'
  | 'integrate'
  | 'automate'
  | 'improve'

export type MethodStep = {
  num: string
  icon: MethodStepIcon
  resultIcon: 'chart' | 'down' | 'database' | 'bolt' | 'loop'
  title: Bilingual
  body: Bilingual
  result: Bilingual
}

export const methodSteps: MethodStep[] = [
  {
    num: '01',
    icon: 'measure',
    resultIcon: 'chart',
    title: { en: 'Measure', fr: 'Mesurer' },
    body: {
      en: 'Understand systems, processes, costs and friction.',
      fr: 'Comprendre systèmes, processus, coûts et irritants.',
    },
    result: {
      en: 'Baseline + starting KPIs',
      fr: 'Portrait de référence + indicateurs de départ',
    },
  },
  {
    num: '02',
    icon: 'simplify',
    resultIcon: 'down',
    title: { en: 'Simplify', fr: 'Simplifier' },
    body: {
      en: 'Remove unnecessary steps, licences and systems.',
      fr: 'Retirer les étapes, licences et systèmes inutiles.',
    },
    result: { en: 'Lower costs', fr: 'Des coûts réduits' },
  },
  {
    num: '03',
    icon: 'integrate',
    resultIcon: 'database',
    title: { en: 'Integrate', fr: 'Intégrer' },
    body: {
      en: 'Connect data, systems and user journeys.',
      fr: 'Connecter données, systèmes et parcours usagers.',
    },
    result: { en: 'Less manual re-entry', fr: 'Moins de ressaisie' },
  },
  {
    num: '04',
    icon: 'automate',
    resultIcon: 'bolt',
    title: { en: 'Automate', fr: 'Automatiser' },
    body: {
      en: 'Add AI where it genuinely creates capacity.',
      fr: 'Ajouter l’IA là où elle crée réellement de la capacité.',
    },
    result: { en: 'More volume handled', fr: 'Plus de volume traité' },
  },
  {
    num: '05',
    icon: 'improve',
    resultIcon: 'loop',
    title: { en: 'Improve', fr: 'Améliorer' },
    body: {
      en: 'Measure results and identify the next opportunity.',
      fr: 'Mesurer les résultats et choisir le prochain gain.',
    },
    result: { en: 'Continuous improvement', fr: 'Amélioration continue' },
  },
]

export const investment = {
  eyebrow: {
    en: 'A measurable investment',
    fr: 'Un investissement mesurable',
  },
  statementLead: { en: 'Lower costs.', fr: 'Moins de coûts.' },
  statementBefore: { en: 'More ', fr: 'Plus de ' },
  statementAccent: { en: 'capacity.', fr: 'capacité.' },
  outcomes: [
    {
      icon: 'database' as const,
      direction: 'down' as const,
      label: { en: 'Unnecessary licences', fr: 'Licences inutiles' },
    },
    {
      icon: 'people' as const,
      direction: 'down' as const,
      label: { en: 'Manual work', fr: 'Travail manuel' },
    },
    {
      icon: 'file' as const,
      direction: 'down' as const,
      label: { en: 'Duplicates', fr: 'Doublons' },
    },
    {
      icon: 'chart' as const,
      direction: 'up' as const,
      label: { en: 'Services delivered', fr: 'Services rendus' },
    },
  ],
  working: [
    {
      icon: 'clock' as const,
      label: { en: '30–45 min meetings', fr: '30 à 45 min par rencontre' },
    },
    {
      icon: 'message' as const,
      label: {
        en: 'One friction point at a time',
        fr: 'Un irritant à la fois',
      },
    },
    {
      icon: 'file' as const,
      label: {
        en: 'Prepared recommendations',
        fr: 'Recommandations préparées',
      },
    },
    {
      icon: 'chart' as const,
      label: {
        en: 'Data-driven decisions',
        fr: 'Décisions fondées sur les données',
      },
    },
  ],
}

export const methodClosing = {
  eyebrow: { en: 'Our commitment', fr: 'Notre engagement' },
  lead: {
    en: 'Transformation that respects your reality.',
    fr: 'Une transformation qui respecte votre réalité.',
  },
  accent: {
    en: 'Real results. At your pace.',
    fr: 'Des résultats concrets. À votre rythme.',
  },
  cta: {
    en: 'Let’s discuss your project',
    fr: 'Parlons de votre projet',
  },
}
